import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from "react"

interface ProfileImageCache {
  [userId: string]: {
    url: string
    timestamp: number
    isLoading: boolean
    error: boolean
  }
}

interface ProfileImageCacheContextType {
  getProfileImage: (userId: string | number) => string | null
  setProfileImage: (userId: string | number, url: string) => void
  setLoading: (userId: string | number, loading: boolean) => void
  setError: (userId: string | number, error: boolean) => void
  isImageCached: (userId: string | number) => boolean
  isLoading: (userId: string | number) => boolean
  hasError: (userId: string | number) => boolean
  clearCache: () => void
  clearUserCache: (userId: string | number) => void
  updateUserImage: (userId: string | number, newUrl: string) => void
}

const ProfileImageCacheContext = createContext<
  ProfileImageCacheContextType | undefined
>(undefined)

export const useProfileImageCache = () => {
  const context = useContext(ProfileImageCacheContext)
  if (!context) {
    throw new Error(
      "useProfileImageCache must be used within a ProfileImageCacheProvider"
    )
  }
  return context
}

interface ProfileImageCacheProviderProps {
  children: ReactNode
}

export const ProfileImageCacheProvider: React.FC<
  ProfileImageCacheProviderProps
> = ({ children }) => {
  const [cache, setCache] = useState<ProfileImageCache>({})

  const getProfileImage = useCallback(
    (userId: string | number): string | null => {
      try {
        const userKey = userId.toString()
        const cached = cache[userKey]

        if (cached && !cached.error) {
          // Verificar si la cache no ha expirado (30 minutos)
          const now = Date.now()
          const cacheAge = now - cached.timestamp
          const cacheExpiry = 30 * 60 * 1000 // 30 minutos

          if (cacheAge < cacheExpiry) {
            return cached.url
          }
        }

        return null
      } catch (error) {
        // En caso de error, retornar null
        return null
      }
    },
    [cache]
  )

  const setProfileImage = useCallback(
    (userId: string | number, url: string) => {
      try {
        const userKey = userId.toString()
        setCache((prev) => ({
          ...prev,
          [userKey]: {
            url,
            timestamp: Date.now(),
            isLoading: false,
            error: false,
          },
        }))
      } catch (error) {
        // En caso de error, no hacer nada
        if (import.meta.env.DEV) {
          console.error("Error setting profile image:", error)
        }
      }
    },
    []
  )

  const setLoading = useCallback(
    (userId: string | number, loading: boolean) => {
      try {
        const userKey = userId.toString()
        setCache((prev) => ({
          ...prev,
          [userKey]: {
            ...prev[userKey],
            isLoading: loading,
          },
        }))
      } catch (error) {
        // En caso de error, no hacer nada
        if (import.meta.env.DEV) {
          console.error("Error setting loading state:", error)
        }
      }
    },
    []
  )

  const setError = useCallback((userId: string | number, error: boolean) => {
    try {
      const userKey = userId.toString()
      setCache((prev) => ({
        ...prev,
        [userKey]: {
          ...prev[userKey],
          error,
          isLoading: false,
        },
      }))
    } catch (error) {
      // En caso de error, no hacer nada
      if (import.meta.env.DEV) {
        console.error("Error setting error state:", error)
      }
    }
  }, [])

  const isImageCached = useCallback(
    (userId: string | number): boolean => {
      try {
        return getProfileImage(userId) !== null
      } catch (error) {
        return false
      }
    },
    [getProfileImage]
  )

  const isLoading = useCallback(
    (userId: string | number): boolean => {
      try {
        const userKey = userId.toString()
        return cache[userKey]?.isLoading || false
      } catch (error) {
        return false
      }
    },
    [cache]
  )

  const hasError = useCallback(
    (userId: string | number): boolean => {
      try {
        const userKey = userId.toString()
        return cache[userKey]?.error || false
      } catch (error) {
        return false
      }
    },
    [cache]
  )

  const clearCache = useCallback(() => {
    try {
      setCache({})
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error("Error clearing cache:", error)
      }
    }
  }, [])

  const clearUserCache = useCallback((userId: string | number) => {
    try {
      const userKey = userId.toString()
      setCache((prev) => {
        const newCache = { ...prev }
        delete newCache[userKey]
        return newCache
      })
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error("Error clearing user cache:", error)
      }
    }
  }, [])

  const updateUserImage = useCallback(
    (userId: string | number, newUrl: string) => {
      try {
        const userKey = userId.toString()
        setCache((prev) => ({
          ...prev,
          [userKey]: {
            url: newUrl,
            timestamp: Date.now(),
            isLoading: false,
            error: false,
          },
        }))
      } catch (error) {
        if (import.meta.env.DEV) {
          console.error("Error updating user image:", error)
        }
      }
    },
    []
  )

  const value: ProfileImageCacheContextType = {
    getProfileImage,
    setProfileImage,
    setLoading,
    setError,
    isImageCached,
    isLoading,
    hasError,
    clearCache,
    clearUserCache,
    updateUserImage,
  }

  return (
    <ProfileImageCacheContext.Provider value={value}>
      {children}
    </ProfileImageCacheContext.Provider>
  )
}
